import React, { lazy, Suspense, useEffect, useState, useRef } from 'react';
import './App.css';
import { Box, Snackbar } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, setDoc, arrayUnion } from 'firebase/firestore';
import { useUser } from './providers/UserProvider';
import Menu from './components/Menu';
import AppBanner from './components/AppBanner';
import { firestore } from './firebaseSetup/firebase';
import { getDeviceFcmToken, saveDeviceFcmToken } from './utils/fcmToken';

// Lazy load dialog components
const LoginModal = lazy(() => import('./components/Login'));
const AddFollower = lazy(() => import('./components/AddFollower'));
const FollowUser = lazy(() => import('./components/FollowUser'));

const App = () => {
  const { user, addFollowerOpen, setAddFollowerOpen, followUserOpen, setFollowUserOpen, setNotificationsDrawerOpen, setTappedNotification } = useUser();
  const [notifications, setNotifications] = useState([]);
  const notificationsInitialized = useRef(false);

  useEffect(() => {
    if (!user?.uid) return () => {};
    if (notificationsInitialized.current) return () => {};
    notificationsInitialized.current = true;

    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    // Helper to save FCM token to Firestore (adds to array, doesn't replace)
    const saveFcmToken = async (token) => {
      if (!token || !user?.uid) return;
      await setDoc(doc(firestore, 'users', user.uid), {
        fcmToken: arrayUnion(token),
      }, { merge: true });
    };

    if (isNative) {
      // Helper to register and wait for token (Android)
      const registerAndWaitForToken = () => new Promise((resolve) => {
        const onRegistration = async (token) => {
          if (token.value) {
            saveDeviceFcmToken(token.value);
            await saveFcmToken(token.value);
          }
          resolve(token.value);
        };
        PushNotifications.addListener('registration', onRegistration);
        PushNotifications.register();
      });

      // Set up other notification listeners
      PushNotifications.addListener('registrationError', (err) => {
        console.warn('Push registration error:', err);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        setNotifications((prev) => [
          ...prev,
          { body: notification.body, title: notification.title, id: Date.now() },
        ]);
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const { data } = action.notification;
        if (data?.type) {
          // Store the tapped notification info for highlighting in the drawer
          setTappedNotification({
            type: data.type,
            fromUid: data.fromUid,
            countUpdateId: data.countUpdateId,
          });
          setNotificationsDrawerOpen(true);
        }
      });

      // Defer permission request to avoid blocking UI
      const timeoutId = setTimeout(async () => {
        try {
          const currentPerm = await PushNotifications.checkPermissions();

          // If already granted, just get the token
          if (currentPerm.receive === 'granted') {
            if (platform === 'android') {
              await registerAndWaitForToken();
            } else {
              await PushNotifications.register();
              // Wait for Firebase to process the APNs token
              await new Promise((resolve) => setTimeout(resolve, 1500));
              const token = await getDeviceFcmToken();
              if (token) await saveFcmToken(token);
            }
            return;
          }

          // If not yet prompted, request permission
          if (currentPerm.receive === 'prompt' || currentPerm.receive === 'prompt-with-rationale') {
            const permResult = await PushNotifications.requestPermissions();
            if (permResult.receive === 'granted') {
              if (platform === 'android') {
                await registerAndWaitForToken();
              } else {
                await PushNotifications.register();
                // Wait for Firebase to process the APNs token
                await new Promise((resolve) => setTimeout(resolve, 1500));
                const token = await getDeviceFcmToken();
                if (token) await saveFcmToken(token);
              }
            }
          }
        } catch (err) {
          console.warn('Push notification init error:', err);
        }
      }, 1500);

      return () => {
        clearTimeout(timeoutId);
        PushNotifications.removeAllListeners();
      };
    }

    // Web push notifications via Firebase
    if ('Notification' in window && 'serviceWorker' in navigator) {
      const timeoutId = setTimeout(async () => {
        try {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') return;

          const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
          const messaging = getMessaging();

          const token = await getToken(messaging, {
            vapidKey: 'BFD_Yxt5y1_b6XhkbTL4HJh6-YpiUxhRE_AwinYi8cRqmhBp_foT6BTNcxKPz4f8VoYy7Co3eHPPuVjasBb5a7g',
          });
          if (token) {
            saveDeviceFcmToken(token);
            await saveFcmToken(token);
          }

          onMessage(messaging, (message) => {
            setNotifications((prev) => [
              ...prev,
              { ...message.notification, id: Date.now() },
            ]);
          });
        } catch (err) {
          console.warn('Push notifications error:', err.message);
        }
      }, 1500);

      return () => clearTimeout(timeoutId);
    }

    return () => {};
  }, [user?.uid, setNotificationsDrawerOpen]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100vw',
        height: '100vh',
        minHeight: '-webkit-fill-available',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <AppBanner />
      <Menu />
      <Outlet />
      <Suspense fallback={null}>
        {addFollowerOpen && <AddFollower open={addFollowerOpen} setOpen={setAddFollowerOpen} />}
        {followUserOpen && <FollowUser open={followUserOpen} setOpen={setFollowUserOpen} />}
        {!user?.uid && <LoginModal show />}
      </Suspense>
      {notifications?.length > 0 && notifications.map((notification, index) => (
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          key={notification.id}
          open
          message={notification.body}
          autoHideDuration={6000}
          onClose={() => setNotifications((prev) => prev.filter((n) => n.id !== notification.id))}
          slotProps={{ content: { sx: { fontFamily: 'quicksand' } } }}
          sx={{
            bottom: { xs: `${24 + index * 60}px`, sm: `${24 + index * 60}px` },
          }}
        />
      ))}
    </Box>
  );
};

export default App;
